import React from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  Navigate
} from "react-router-dom";

import * as Cognito from "./providers/cognito";
import * as User from "./providers/user";

import Login from "./routes/Login";
import CompanyName from "./routes/CompanyName";
import NotFound from "./routes/NotFound";
import Dashboard from "./routes/Dashboard";
import Verify from "./routes/Verify";
import UserName from "./routes/UserName";
import Register from "./routes/Register";
import ResetPassword from "routes/ResetPassword";
import ResetPasswordVerify from "routes/VerifyResetPassword";
import SetNewPassword from "./routes/SetNewPassword";
import RegisterArtist from "routes/RegisterArtist";
import TermsAndConditions from "routes/TermsAndConditions";
import PrivacyPolicy from "routes/PrivacyPolicy";
import CookiesNotice from "routes/CookiesNotice";

const approvedRoles = ["OWNER", "MANAGER", "ARTIST", "SUPER"];

export default function Router() {
  const {
    loading,
    user: cognitoUser,
    isFetchingUserFromDb
  } = Cognito.useCognito();
  const { user } = User.useUser();

  if (loading || user === undefined)
    return <div className="h-screen bg-mono-primaryBgColor" />;

  return (
    <BrowserRouter>
      <React.Suspense fallback={null}>
        <Routes>
          <Route
            path="/register"
            element={
              <NoAuth
                cognitoUser={cognitoUser}
                user={user}
                isFetchingUserFromDb={isFetchingUserFromDb}
              >
                <Register />
              </NoAuth>
            }
          />
          <Route
            path="/register-artist"
            element={
              <NoAuth
                cognitoUser={cognitoUser}
                user={user}
                isFetchingUserFromDb={isFetchingUserFromDb}
              >
                <RegisterArtist />
              </NoAuth>
            }
          />
          <Route
            path="/login"
            element={
              <NoAuth
                cognitoUser={cognitoUser}
                user={user}
                isFetchingUserFromDb={isFetchingUserFromDb}
              >
                <Login />
              </NoAuth>
            }
          />
          <Route path="/toc" element={<TermsAndConditions />} />
          <Route path="/privacypolicy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiesNotice />} />
          <Route path="/verify" element={<Verify />} />
          <Route path="/set-new-password" element={<SetNewPassword />} />
          <Route
            path="/forgot-password"
            element={
              <NoAuth
                cognitoUser={cognitoUser}
                user={user}
                isFetchingUserFromDb={isFetchingUserFromDb}
              >
                <ResetPassword />
              </NoAuth>
            }
          />
          <Route path="/verify-reset" element={<ResetPasswordVerify />} />
          <Route
            path={`/dashboard/*`}
            element={
              <RequireAuth user={cognitoUser}>
                <Dashboard />
              </RequireAuth>
            }
          />
          <Route
            path={`/on-boarding/name`}
            element={
              <RequireAuth user={cognitoUser}>
                <UserName />
              </RequireAuth>
            }
          />
          <Route
            path={`/on-boarding/company`}
            element={
              <RequireAuth user={cognitoUser}>
                <CompanyName />
              </RequireAuth>
            }
          />
          <Route
            path="/"
            element={
              <Navigate
                to={
                  user?.id
                    ? user.first_name && user.last_name
                      ? (user.company || user.joined_company_id) &&
                        approvedRoles.includes(user.role.role)
                        ? "/dashboard"
                        : "/on-boarding/company"
                      : "/on-boarding/name"
                    : "/login"
                }
                replace
              />
            }
          />
          <Route
            path="/Availability/create"
            element={<Navigate to="/dashboard/Availability/create" />}
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </React.Suspense>
    </BrowserRouter>
  );
}

interface NoAuthProps {
  children: JSX.Element;
  user: any | null;
  cognitoUser: any | null;
  isFetchingUserFromDb: boolean;
}

export function NoAuth(props: NoAuthProps) {
  const { cognitoUser, user, isFetchingUserFromDb } = props;
  const location = useLocation();

  if (user?.id && user.first_name && user.last_name && user.company) {
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }

  // if artist and no company
  if (
    user?.id &&
    user.first_name &&
    user.last_name &&
    !user.company &&
    user.joined_company_id &&
    approvedRoles.includes(user.role.role)
  ) {
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }

  if (user?.id && user.first_name && user.last_name && !user.company) {
    return (
      <Navigate to="/on-boarding/company" state={{ from: location }} replace />
    );
  }

  if (user?.id && (!user.first_name || !user.last_name)) {
    return (
      <Navigate to="/on-boarding/name" state={{ from: location }} replace />
    );
  }

  if (cognitoUser?.id && !user?.id) {
    if (isFetchingUserFromDb)
      return <div className="h-screen bg-mono-primaryBgColor" />;

    return (
      <Navigate to="/on-boarding/name" state={{ from: location }} replace />
    );
  }

  return props.children;
}

interface RequireAuthProps {
  children: JSX.Element;
  user: any | null;
}

export function RequireAuth(props: RequireAuthProps) {
  const { user } = props;
  const location = useLocation();

  if (!user?.id) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return props.children;
}
